import React, { useEffect } from "react";
import FormInput from "../widgets/FormInput";
import FormSelect from "../widgets/FormSelect";
import * as Yup from "yup";
import { Formik, useFormikContext } from "formik";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { getTags } from "src/api/settings";
import { Button } from "../ui/button";
import { addAccount, getBanks, updateAccount, validateAccount } from "src/api/accounts";
import { showSuccessToast } from "src/lib/utils";
import { usePortalContext } from "src/context/PortalContext";

const validationSchema = Yup.object().shape({
	bank_name: Yup.string().required("Bank name is required"),
	account_number: Yup.string()
		.matches(/^\d{10}$/, "Account number must be exactly 10 digits")
		.required("Account number is required"),
	account_name: Yup.string().required("Account name is required"),
	bank_code: Yup.string().required("Bank code is required"),
	priority: Yup.number().min(0, "Minimum amount is zero").max(100, "Maximum amount is 100").required("Percentage share is required"),
	tag_id: Yup.string().required("Tag is required"),
});

const NewAccountModal = ({ account }) => {
	const closePortal = usePortalContext();
	const queryClient = useQueryClient();

	const initialValues = {
		...(account && { uuid: account?.uuid }),
		bank_name: account?.bank_name ?? "",
		account_number: account?.account_number ?? "",
		account_name: account?.account_name ?? "",
		bank_code: account?.bank_code ?? "",
		priority: account?.priority ?? "",
		tag_id: account?.tag_id ?? "",
	};

	const { mutate, isLoading } = useMutation(account ? updateAccount : addAccount, {
		onSuccess: ({ message }) => {
			showSuccessToast(message);
			queryClient.invalidateQueries(["accounts"]);
			closePortal();
		},
	});

	const [{ data: rawTags }, { data: rawBanks }] = useQueries({
		queries: [
			{
				queryKey: ["tags"],
				queryFn: getTags,
				suspense: true,
			},
			{
				queryKey: ["banks"],
				queryFn: () => getBanks(),
				suspense: true,
			},
		],
	});

	const tags = rawTags?.data?.map((tag) => ({
		value: tag.id?.toString() || "",
		label: tag.name || "Unknown",
	}));
	const banks = rawBanks?.data?.map((tag) => ({
		value: tag.code,
		label: tag.name || "Unknown",
	}));

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-5">
					<div className="tw-grid tw-gap-5 tw-grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
						<FormSelect
							className="formItem !tw-w-full !tw-m-0"
							label="Bank"
							value={values.bank_code}
							error={(touched.bank_code || touched.bank_name) && (errors.bank_code ?? errors.bank_name)}
							options={banks}
							onChange={(selectedOption) => {
								setFieldValue("bank_code", selectedOption.value);
								setFieldValue("bank_name", selectedOption.label);
							}}
						/>
						<FormInput
							className="formItem !tw-w-full !tw-m-0"
							label="Account Number"
							name="account_number"
							type="text"
							placeholder="Account Number"
							value={values.account_number}
							error={touched.account_number && errors.account_number}
							onChange={handleChange}
						/>
					</div>
					<ValidateAccount />
					<div className="tw-grid tw-gap-5 tw-grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
						<FormInput
							className="formItem !tw-w-full !tw-m-0"
							label="Percentage share"
							name="priority"
							type="text"
							placeholder="Percentage share"
							value={values.priority}
							error={touched.priority && errors.priority}
							onChange={handleChange}
						/>

						<FormSelect
							className="formItem !tw-w-full !tw-m-0"
							label="Tag"
							name="tag_id"
							value={values.tag_id}
							error={touched.tag_id && errors.tag_id}
							options={tags}
							onChange={(selectedOption) => setFieldValue("tag_id", selectedOption.value)}
						/>
					</div>

					{account ? (
						<Button type="submit" className="" variant="secondary" disabled={isLoading}>
							{isLoading ? "Updating..." : "Update Bank Account"}
						</Button>
					) : (
						<Button type="submit" className="" variant="secondary" disabled={isLoading}>
							{isLoading ? "Adding..." : "Add Bank Account"}
						</Button>
					)}
				</form>
			)}
		</Formik>
	);
};

const ValidateAccount = () => {
	const { values, errors, setFieldValue, touched, setFieldTouched } = useFormikContext();

	const { data, isFetching } = useQuery({
		queryKey: ["validate-account", values.bank_code, values.account_number],
		queryFn: () => validateAccount({ accountNumber: values.account_number, bankCode: values.bank_code }),
		enabled: !!values.bank_code && values.account_number.length === 10,
	});

	useEffect(() => {
		if (data?.data?.accountName) {
			setFieldValue("account_name", data?.data?.accountName, false);
		} else {
			setFieldValue("account_name", null, false);
			setFieldTouched("account_name", false);
		}
	}, [data, setFieldTouched, setFieldValue]);

	// if (!values.bank_code || values.account_number.length !== 10 || !values.account_name) return null;

	if (isFetching)
		return (
			<FormInput className="formItem !tw-w-full !tw-m-0" label="Account Name" type="text" placeholder="Account Name" value="Loading" disabled />
		);

	return (
		<FormInput
			className="formItem !tw-w-full !tw-m-0"
			label="Account Name"
			name="account_name"
			type="text"
			placeholder="Account Name"
			value={values.account_name}
			error={touched.account_name && errors.account_name}
			disabled
		/>
	);
};

export default NewAccountModal;
